import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { config } from "@/config";
import { DEFAULT_AUTH_METHODS, parseAuthMethods, type AuthMethod } from "@/config";
import { loginWithGoogle } from "@/services/auth";
import { pickLocale, stringsFor, type Strings } from "@/strings";
import { AnimatedBackground } from "./AnimatedBackground";
import { WhatsAppLogin } from "./WhatsAppLogin";

/**
 * Consumer apps (LPs, riders, corporate, etc.) decide which auth methods to
 * render by passing `?method=` (or `?methods=`) in the redirect to auth-core.
 * If nothing is passed, we fall back to Google only — auth-core has no
 * deploy-wide override on purpose, so the contract stays the same across
 * environments.
 *
 *   ?method=whatsapp                → WhatsApp only
 *   ?method=google,whatsapp         → both, with the "ou" divider
 *   (omitted / invalid)             → ["google"]
 */
const resolveAuthMethods = (): AuthMethod[] => {
  if (typeof window === "undefined") return [...DEFAULT_AUTH_METHODS];
  const params = new URLSearchParams(window.location.search);
  const fromUrl =
    parseAuthMethods(params.get("method")) ??
    parseAuthMethods(params.get("methods"));
  return fromUrl ?? [...DEFAULT_AUTH_METHODS];
};

type MessageType = "error" | "success";

interface MessageState {
  text: string;
  type: MessageType;
}

const GoogleButton: React.FC<{
  isLoading: boolean;
  setIsLoading: (v: boolean) => void;
  onError: (msg: string) => void;
  strings: Strings;
}> = ({ isLoading, setIsLoading, onError, strings }) => {
  const [isGoogleReady, setIsGoogleReady] = useState(false);
  const buttonRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const checkGoogleLoaded = (): void => {
      if (typeof window === "undefined") return;
      if (window.google?.accounts?.id) {
        setIsGoogleReady(true);
        return;
      }
      setTimeout(checkGoogleLoaded, 100);
    };

    const script = document.querySelector(
      'script[src*="accounts.google.com/gsi/client"]'
    );
    if (script) {
      if (script.getAttribute("data-loaded") === "true") {
        checkGoogleLoaded();
      } else {
        script.addEventListener("load", () => {
          script.setAttribute("data-loaded", "true");
          checkGoogleLoaded();
        });
        checkGoogleLoaded();
      }
    } else {
      checkGoogleLoaded();
    }
  }, []);

  useEffect(() => {
    if (!buttonRef.current || !window.google?.accounts?.id || !isGoogleReady) {
      return;
    }

    window.google.accounts.id.initialize({
      client_id: config.googleClientId,
      callback: async (response: { credential: string }) => {
        const idToken = response.credential;
        if (!idToken) {
          onError(strings.googleNoToken);
          setIsLoading(false);
          return;
        }

        setIsLoading(true);
        try {
          await loginWithGoogle(idToken, strings);
        } catch (error) {
          onError(error instanceof Error ? error.message : strings.googleSignInFailed);
          setIsLoading(false);
        }
      },
    });

    setTimeout(() => {
      try {
        window.google?.accounts?.id?.prompt((notification) => {
          if (notification.isNotDisplayed) {
            console.log("Auto-prompt not displayed");
          }
          if (notification.isDismissedMoment) {
            console.log("Auto-prompt dismissed");
          }
        });
      } catch (error) {
        console.warn("Auto-prompt failed:", error);
      }
    }, 300);

    window.google.accounts.id.renderButton(buttonRef.current, {
      theme: "outline",
      size: "large",
      text: "continue_with",
      locale: strings.googleButtonLocale,
    });
  }, [isGoogleReady, onError, setIsLoading, strings]);

  return (
    <div
      ref={buttonRef}
      style={{
        display: "flex",
        justifyContent: "center",
        opacity: isLoading ? 0.7 : 1,
        pointerEvents: isLoading ? "none" : "auto",
      }}
    />
  );
};

const App: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState<MessageState | null>(null);

  const showError = useCallback((text: string): void => {
    setMessage({ text, type: "error" });
  }, []);
  const clearMessage = useCallback((): void => {
    setMessage(null);
  }, []);

  const authMethods = useMemo(() => resolveAuthMethods(), []);
  const strings = useMemo(() => stringsFor(pickLocale(authMethods)), [authMethods]);
  const showGoogle = authMethods.includes("google");
  const showWhatsApp = authMethods.includes("whatsapp");
  const showBoth = showGoogle && showWhatsApp;

  return (
    <div className="min-h-screen relative overflow-hidden">
      <AnimatedBackground />

      <main
        className="relative z-10 flex flex-col items-center justify-center min-h-screen p-4"
        role="main"
        aria-labelledby="login-heading"
      >
        <div className="w-full max-w-[440px] opacity-0 animate-fade-in-up">
          {/* Logo */}
          <div className="text-center mb-10 opacity-0 animate-fade-in-down animation-delay-200">
            <div className="inline-flex items-center justify-center p-5 rounded-3xl logo-container-premium mb-6">
              <img
                src="/assets/main-logo.png"
                alt={strings.logoAlt}
                className="brightness-0 invert"
                width={180}
                height={56}
                style={{
                  display: "block",
                  width: "180px",
                  height: "56px",
                  objectFit: "contain",
                }}
              />
            </div>
            <h1 id="login-heading" className="sr-only">
              {strings.pageTitleSr}
            </h1>
          </div>

          {/* Login Card */}
          <div className="card-premium rounded-[28px] p-7 relative opacity-0 scale-95 animate-scale-in animation-delay-300">
            {/* Subtle glow effect */}
            <div
              className="absolute pointer-events-none -z-10"
              style={{
                top: "-50%",
                left: "-50%",
                right: "-50%",
                bottom: "-50%",
                background:
                  "radial-gradient(circle at 50% 0%, rgba(46, 194, 255, 0.15) 0%, transparent 50%)",
              }}
            />

            {/* Header */}
            <div className="text-center mb-9">
              <h2 className="text-[2rem] font-bold tracking-tight text-gradient-white">
                {strings.welcomeBack}
              </h2>
            </div>

            {/* Auth methods */}
            <div className="opacity-0 animate-fade-in-up animation-delay-500 flex flex-col gap-5">
              {showGoogle && (
                <GoogleButton
                  isLoading={isLoading}
                  setIsLoading={setIsLoading}
                  onError={showError}
                  strings={strings}
                />
              )}

              {showBoth && (
                <div className="flex items-center gap-4">
                  <div className="flex-1 h-px bg-gradient-to-r from-transparent via-white/15 to-transparent" />
                  <span className="text-white/35 text-[0.7rem] uppercase tracking-[0.15em] font-medium">
                    {strings.orDivider}
                  </span>
                  <div className="flex-1 h-px bg-gradient-to-r from-transparent via-white/15 to-transparent" />
                </div>
              )}

              {showWhatsApp && (
                <WhatsAppLogin
                  onError={showError}
                  onClearMessage={clearMessage}
                />
              )}
            </div>

            {/* Error/Success Message */}
            {message && (
              <div
                className={`mt-4 p-3 rounded-lg text-sm opacity-0 animate-fade-in animation-delay-600 ${
                  message.type === "error"
                    ? "bg-red-500/20 text-red-200 border border-red-500/30"
                    : "bg-green-500/20 text-green-200 border border-green-500/30"
                }`}
              >
                {message.text}
              </div>
            )}

            {/* Divider */}
            <div className="flex items-center gap-4 my-9 opacity-0 animate-fade-in animation-delay-600">
              <div className="flex-1 h-px bg-gradient-to-r from-transparent via-white/15 to-transparent" />
              <span className="text-white/35 text-[0.7rem] uppercase tracking-[0.15em] font-medium">
                {strings.secureAccess}
              </span>
              <div className="flex-1 h-px bg-gradient-to-r from-transparent via-white/15 to-transparent" />
            </div>

            {/* Security badges */}
            <div className="flex justify-center gap-8 mb-7 opacity-0 animate-fade-in animation-delay-700">
              <div className="flex items-center gap-2 px-3 py-2 rounded-lg security-badge">
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="rgba(46, 194, 255, 0.9)"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                  <path d="m9 12 2 2 4-4" />
                </svg>
                <span className="text-white/60 text-[0.7rem] font-medium">
                  {strings.sslSecured}
                </span>
              </div>
              <div className="flex items-center gap-2 px-3 py-2 rounded-lg security-badge">
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="rgba(46, 194, 255, 0.9)"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                  <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                </svg>
                <span className="text-white/60 text-[0.7rem] font-medium">
                  {strings.encrypted}
                </span>
              </div>
            </div>

            {/* Terms */}
            <p className="text-center text-xs text-white/40 leading-relaxed opacity-0 animate-fade-in animation-delay-800">
              {strings.termsBefore}
              <span className="text-[rgba(46,194,255,0.9)] cursor-pointer font-medium hover:text-[rgba(46,194,255,1)] transition-colors">
                {strings.termsLink}
              </span>
              {strings.termsBetween}
              <span className="text-[rgba(46,194,255,0.9)] cursor-pointer font-medium hover:text-[rgba(46,194,255,1)] transition-colors">
                {strings.privacyLink}
              </span>
            </p>
          </div>

          {/* Footer */}
          <p className="text-center text-xs text-white/25 mt-10 tracking-wide opacity-0 animate-fade-in animation-delay-1000">
            {strings.copyright(new Date().getFullYear())}
          </p>
        </div>
      </main>
    </div>
  );
};

export default App;
